import moment from "moment";

export const Debounce = (fn, t) => {
  const delay = t || 300;
  let timeout;
  return function () {
    const context = this;
    const args = arguments;
    if (!timeout) {
      timeout = setTimeout(() => {
        timeout = null;
        fn.apply(context, args);
      }, delay);
    }
  };
};

/**
 * 防抖
 * @param {*} fun 防抖函数
 * @param {*} delay 延迟时间
 * @param {*} timerid 延时id [可选参数] 若传参将固定timer的id
 */
export function debounce(fun, delay, timerid) {
  return function (args) {
    let that = this;
    let _args = args;
    let timer = timerid || fun.id;
    clearTimeout(timer);
    timer = setTimeout(function () {
      fun.call(that, _args);
    }, delay);
  };
}

/**
 * 数值格式化
 * @param {*} num
 * @param {*} n
 * @returns
 */
export const tofix = (num, n) => {
  const arr = num.toString().split(".");
  const integer = arr[0];
  let decimal = arr[1] || "";

  if (decimal.length > n) {
    decimal = decimal.substring(0, n);
  } else {
    const index = n - decimal.length;
    for (let i = 0; i < index; i++) {
      decimal = `${decimal}0`;
    }
  }
  return n === 0 ? `${integer}` : `${integer}.${decimal}`;
};

/**
 * 节流函数
 * @param {*} func
 * @param {*} delay
 * @returns
 */
export const throttle = (func, delay) => {
  let timeoutId;
  let lastExecTime = 0;
  let pending = false;

  return function (...args) {
    const currentTime = Date.now();

    if (!pending) {
      // 如果没有待执行的函数
      const timeSinceLastExec = currentTime - lastExecTime;
      if (timeSinceLastExec >= delay) {
        // 已经达到设定的时间间隔，可以执行函数
        lastExecTime = currentTime;
        func.apply(this, args);
      } else {
        // 还未达到设定的时间间隔，设置定时器延迟执行函数
        timeoutId = setTimeout(() => {
          lastExecTime = currentTime;
          func.apply(this, args);
        }, delay - timeSinceLastExec);
        pending = true;
      }
    } else {
      // 如果有待执行的函数，取消之前的定时器并设置新的定时器
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        lastExecTime = currentTime;
        func.apply(this, args);
        pending = false;
      }, delay);
    }
  };
};

/**
 * 获取值首次出现的位置
 * @param {*} array 需要查找的数组
 * @param {*} field 匹配字段
 * @param {*} value 匹配字段-目标值
 * @returns
 */
export const findIndexByFieldValue = (array, field, value) => {
  return array.findIndex((item) => item[field] === value);
};

/**
 * 获取值最后一次出现的位置
 * @param {*} array 需要查找的数组
 * @param {*} field 匹配字段
 * @param {*} value 匹配字段-目标值
 * @returns
 */
export const findLastIndexByFieldValue = (array, field, value) => {
  for (let i = array.length - 1; i >= 0; i--) {
    if (array[i][field] === value) {
      return i;
    }
  }
  return -1; // 如果未找到匹配项，则返回 -1 表示未找到
};

/**
 * 过渡执行
 * @param {*} from 起始值
 * @param {*} to 目标值
 * @param {*} duration 执行总时长
 * @param {*} fun 执行函数
 * @param {*} speed 速度帧
 */
export const animateNumber = (
  from,
  to,
  duration,
  fun = () => {},
  speed = 10
) => {
  const increment = (to - from) / (duration / speed);
  let currentNumber = from;
  function updateNumber() {
    fun(currentNumber);
    if (currentNumber < to) {
      currentNumber += increment;
      setTimeout(updateNumber, speed); // 每10毫秒更新下一个数字
    }
  }
  updateNumber();
};

/**
 * px to rem 自适应转换工具
 * @param {css_px} px 像素
 * @param {number} rootValue 比例，默认37.5
 */
export const px2rem = (px, rootValue = 37.5) => {
  if (/%/gi.test(px)) {
    // 处理%传参情况，如90%
    return px;
  } else {
    return parseFloat(px) / rootValue + "rem";
  }
};

/**
 * 时间格式转换
 * @param time
 * @param dateFormat
 */
export function getTime(time, dateFormat = "YYYY-MM-DD HH:mm:ss") {
  if (
    new Date(time).getTime() === 0 ||
    time === "1970-01-01T08:00:01+08:00" ||
    time === "0001-01-01T00:00:00Z"
  ) {
    return "";
  }
  return moment(time).utcOffset(0).format(dateFormat);
}

/**
 * toFixed (无进制)
 * @param {*} number 需要改变的数字
 * @param {*} tofixNum 保留位数
 */
export function toFixedWithMode(number, tofixNum) {
  const numberString = number.toString();
  const isPointNum = numberString?.indexOf(".") > -1;

  if (!isPointNum) {
    // 非小数
    let endNum = "";
    for (let i = 0; i < tofixNum; i++) endNum += 0;
    return numberString + "." + endNum;
  } else {
    // 含小数
    const d = numberString.split(".")[0];
    const p = numberString.split(".")[1];
    if (p.length >= tofixNum) {
      return d + "." + p.slice(0, tofixNum);
    } else {
      let endNum = "";
      for (let i = p.length; i < tofixNum; i++) endNum += 0;
      return d + "." + p + endNum;
    }
  }
}

/**
 * 数字格式化
 */
export function NumberFormat(val, leftNum) {
  const num = 1000;
  const CLIP_LENGTH = leftNum || 1;
  let sizesValue = "";
  /**
   * 判断取哪个单位
   */
  if (val < 1000) {
    // 如果小于1000则直接返回
    sizesValue = "";
    let useReturnVal = `${val}`;
    const NumArr = useReturnVal.split(".");
    if (NumArr.length > 1 && NumArr?.[1]?.length > CLIP_LENGTH) {
      NumArr[1] = NumArr[1].charAt(0);
      useReturnVal = NumArr.join(".");
    }
    return useReturnVal;
  } else if (val >= 1000 && val < 999999) {
    sizesValue = "K";
  } else if (val > 999999 && val < 999999999) {
    sizesValue = "M";
  } else if (val > 999999999) {
    sizesValue = "B";
  }
  /**
   * 大于一万则运行下方计算
   */
  const i = Math.floor(Math.log(val) / Math.log(num));

  let sizes = val / Math.pow(num, i);
  sizes = toFixedWithMode(sizes, CLIP_LENGTH);
  sizes = sizes + sizesValue;
  return sizes;
}

/**
 * 文件下载
 */
export function DownloadFile(url, responseFileType = "json") {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const useUrl = url + `?nowtimestarp=${new Date().getTime()}`;
    xhr.open("GET", useUrl);
    xhr.responseType = responseFileType;

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve(xhr.response);
      } else {
        reject(new Error("Failed to download"));
      }
    };

    xhr.onerror = () => {
      reject(new Error("Failed to download"));
    };

    xhr.send();
  });
}

/**文字截取显示 */
export function truncateString(str, maxLength) {
  if (str.length <= maxLength) {
    return str; // 字符串长度小于或等于maxLength，无需截断
  }

  const ellipsis = "...";
  const startLength = Math.floor((maxLength - ellipsis.length) / 2);
  const endLength = maxLength - startLength - ellipsis.length;

  const truncatedStr =
    str.substring(0, startLength) +
    ellipsis +
    str.substring(str.length - endLength);
  return truncatedStr;
}

/**
 * 字符串首字母大写
 * @param param0
 * @returns
 */
export const toFirstUpper = (str) => {
  if (!str) return "";

  const list = [...str];
  const [first, ...rest] = list;
  return first.toUpperCase() + rest.join("");
};
